//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';

/**
 * @service AccountClosureService
 * @extends Service<any>
 * @description
 * Service for managing account closure related operations and data retrieval.
 *
 * This service interacts with the `/tradingcommunity/aqucloudaccounts/` endpoint to fetch
 * details related to business accounts undergoing closure or status changes.
 *
 * ## Key Responsibilities:
 * 1. **Data Retrieval**: Fetches business persons associated with a specific account ID.
 * 2. **Request Management**: Tracks and cancels ongoing HTTP requests to manage resources efficiently.
 *
 * ## API Endpoints:
 * - Base URL: `/tradingcommunity/aqucloudaccounts/`
 */
@Injectable({
  providedIn: 'root'
})

export class AccountClosureService extends Service<any> {

  /**
   * @type {Subscription[]}
   * @description
   * Array to store ongoing HTTP request subscriptions.
   * Used to cancel requests when the service is destroyed or when operations need to be aborted.
   * @default []
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructor for AccountClosureService.
   * Initializes the service with the HttpClient and sets the base URL for account closure API.
   *
   * @param {HttpClient} httpClient - Angular HttpClient for making HTTP requests.
   * @service HttpClient
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/tradingcommunity/aqucloudaccounts/');
  }

  /**
   * @method getBusinessPersonsByAccountId
   * @async
   * @description
   * Retrieves a list of business persons associated with a specific business account ID.
   *
   * ## Logic Flow:
   * 1. Constructs a GET request to the base URL.
   * 2. Appends query parameters:
   *    - `_id`: The provided business account ID.
   *    - `include`: Specifies related data to include (business.persons.personID>firstName lastName personType.name).
   * 3. Subscribes to the observable and converts it to a Promise.
   * 4. Pushes the subscription to `ongoingRequests` for management.
   *
   * @param {string} business_account_id - The unique identifier of the business account.
   * @returns {Promise<Array<any>>} A promise that resolves to an array of business person data.
   * @throws {HttpErrorResponse} If the API call fails.
   * @side-effect Adds a subscription to `ongoingRequests`.
   */
  public getBusinessPersonsByAccountId(business_account_id: string, ): Promise<Array<any>> {
    return new Promise<any>((resolve, reject) => {
      const request = this.httpClient.get<any>(`${this.baseUrl}${this.serviceUrl}?_id=${business_account_id}&include=business.persons.personID>firstName lastName personType.name`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description
   * Cancels all currently active HTTP requests initiated by this service.
   *
   * ## Logic Flow:
   * 1. Iterates through the `ongoingRequests` array.
   * 2. Checks if each item is a valid subscription with an `unsubscribe` method.
   * 3. Calls `unsubscribe()` to cancel the request.
   * 4. Resets the `ongoingRequests` array to empty.
   *
   * Use this method in `ngOnDestroy` of components using this service to prevent memory leaks.
   *
   * @returns {void}
   * @side-effect Unsubscribes from Observables and clears `ongoingRequests`.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
